// Product image viewer — full-screen lightbox for the PDP gallery.
//
// Tap/click a gallery photo to open it full screen, then:
//   - slide: swipe (touch), the arrow buttons, or ←/→ on the keyboard;
//   - zoom, touch: pinch (up to 4×), double-tap to toggle 2.5×, drag to
//     pan while zoomed (swiping is suspended until zoomed back out);
//   - zoom, mouse: click to zoom 2.5× at that point, the image then
//     follows the cursor; click again to zoom out.
//
// Slides are built from the gallery the click came from (the PDP renders
// a second, desktop-only copy of the gallery), from photos only — videos
// and 3D models are skipped — and only those currently visible, so
// variant-hidden media stays out. Shoppable photos (with product tags)
// are included in the sequence but don't open the viewer on tap, so the
// tags stay tappable.
//
// Loaded from sections/main-product.liquid when the section's "Image
// viewer" setting is on. Styles: assets/component-image-viewer.css.
(function () {
  if (window.ProductImageViewer) return;

  const MAX_SCALE = 4;
  const TAP_ZOOM = 2.5;
  const DOUBLE_TAP_MS = 300;

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  // Shopify CDN URL for the same file at another width.
  function sizedUrl(src, width) {
    const url = new URL(src, window.location.href);
    url.searchParams.set('width', width);
    return url.href;
  }

  function srcsetFor(src) {
    return [1100, 1600, 2400, 3200].map((w) => `${sizedUrl(src, w)} ${w}w`).join(', ');
  }

  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

  class Viewer {
    constructor() {
      this.index = 0;
      this.slides = [];
      this.zoom = { scale: 1, x: 0, y: 0 };
      this.build();
    }

    build() {
      const root = document.createElement('div');
      root.className = 'image-viewer';
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      root.setAttribute('aria-label', 'Product images');
      root.hidden = true;
      root.innerHTML = `
        <div class="image-viewer__bar">
          <span class="image-viewer__counter" aria-live="polite"></span>
          <button type="button" class="image-viewer__close" aria-label="Close">
            <span class="image-viewer__close-label">Close</span>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="image-viewer__track"></div>
        <button type="button" class="image-viewer__arrow image-viewer__arrow--prev" aria-label="Previous image">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10.5 2.5L5 8l5.5 5.5" stroke="currentColor" stroke-width="1"/></svg>
        </button>
        <button type="button" class="image-viewer__arrow image-viewer__arrow--next" aria-label="Next image">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M5.5 2.5L11 8l-5.5 5.5" stroke="currentColor" stroke-width="1"/></svg>
        </button>
      `;
      document.body.appendChild(root);

      this.root = root;
      this.track = root.querySelector('.image-viewer__track');
      this.counter = root.querySelector('.image-viewer__counter');

      root.querySelector('.image-viewer__close').addEventListener('click', () => this.close());
      root.querySelector('.image-viewer__arrow--prev').addEventListener('click', () => this.go(this.index - 1));
      root.querySelector('.image-viewer__arrow--next').addEventListener('click', () => this.go(this.index + 1));

      this.onKeydown = this.onKeydown.bind(this);
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });

      // Mouse zoom: click toggles, move pans.
      this.track.addEventListener('click', (e) => this.onClick(e));
      this.track.addEventListener('mousemove', (e) => this.onMouseMove(e));

      // Touch zoom: pinch, double-tap, pan. Non-passive so pinch/pan can
      // stop the page from zooming and the track from swiping.
      this.track.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: false });
      this.track.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false });
      this.track.addEventListener('touchend', (e) => this.onTouchEnd(e));
      // iOS Safari's own pinch-to-zoom of the page.
      root.addEventListener('gesturestart', (e) => e.preventDefault());
    }

    open(images, startIndex) {
      this.track.innerHTML = '';
      this.slides = images.map((src, i) => {
        const slide = document.createElement('div');
        slide.className = 'image-viewer__slide';
        const frame = document.createElement('div');
        frame.className = 'image-viewer__zoom';
        const img = document.createElement('img');
        img.alt = '';
        img.decoding = 'async';
        img.draggable = false;
        img.sizes = '100vw';
        // Current and direct neighbours load now; the rest when reached.
        if (Math.abs(i - startIndex) > 1) img.loading = 'lazy';
        img.srcset = srcsetFor(src);
        img.src = sizedUrl(src, 1600);
        frame.appendChild(img);
        slide.appendChild(frame);
        this.track.appendChild(slide);
        return { slide, frame, img };
      });

      this.root.classList.toggle('image-viewer--single', images.length < 2);
      this.root.hidden = false;
      document.documentElement.classList.add('image-viewer-open');
      document.addEventListener('keydown', this.onKeydown);
      this.lastFocus = document.activeElement;

      this.index = startIndex;
      this.resetZoom();
      // Jump straight to the clicked photo, no scroll animation.
      requestAnimationFrame(() => {
        this.track.scrollTo({ left: startIndex * this.track.clientWidth, behavior: 'instant' });
        this.updateCounter();
        this.root.querySelector('.image-viewer__close').focus({ preventScroll: true });
      });
    }

    close() {
      this.resetZoom();
      this.root.hidden = true;
      this.track.innerHTML = '';
      this.slides = [];
      document.documentElement.classList.remove('image-viewer-open');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    go(index) {
      if (!this.slides.length) return;
      const target = clamp(index, 0, this.slides.length - 1);
      this.resetZoom();
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'smooth' });
    }

    onScroll() {
      const index = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
      if (index !== this.index) {
        this.resetZoom();
        this.index = index;
        this.updateCounter();
      }
    }

    updateCounter() {
      // Load the photos either side now, so the next swipe never lands on
      // a blank slide while a lazy image is still waiting to start.
      [this.index - 1, this.index, this.index + 1].forEach((i) => {
        const s = this.slides[i];
        if (s && s.img.loading === 'lazy') s.img.loading = 'eager';
      });
      this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
      this.root.classList.toggle('image-viewer--first', this.index === 0);
      this.root.classList.toggle('image-viewer--last', this.index === this.slides.length - 1);
    }

    onKeydown(e) {
      if (e.key === 'Escape') this.close();
      else if (e.key === 'ArrowLeft') this.go(this.index - 1);
      else if (e.key === 'ArrowRight') this.go(this.index + 1);
    }

    // ---- zoom state -------------------------------------------------

    current() {
      return this.slides[this.index];
    }

    resetZoom() {
      this.zoom = { scale: 1, x: 0, y: 0 };
      this.slides.forEach((s) => {
        s.frame.style.transform = '';
        s.slide.classList.remove('is-zoomed');
      });
      this.root.classList.remove('image-viewer--zoomed');
    }

    // Keep the zoomed image covering the frame: no panning past its edges.
    // The <img> is sized to the photo itself (not the slide), so its box
    // is the real image area.
    clampPan() {
      if (!this.current()) return;
      const limits = this.panLimits();
      this.zoom.x = clamp(this.zoom.x, -limits.x, limits.x);
      this.zoom.y = clamp(this.zoom.y, -limits.y, limits.y);
    }

    applyZoom() {
      const cur = this.current();
      if (!cur) return;
      if (this.zoom.scale <= 1.01) {
        this.resetZoom();
        return;
      }
      this.clampPan();
      cur.frame.style.transform = `translate(${this.zoom.x}px, ${this.zoom.y}px) scale(${this.zoom.scale})`;
      cur.slide.classList.add('is-zoomed');
      this.root.classList.add('image-viewer--zoomed');
    }

    // Zoom to `scale` keeping the point (px, py) — viewport coords — fixed.
    zoomAt(scale, px, py) {
      const cur = this.current();
      if (!cur) return;
      const box = cur.slide.getBoundingClientRect();
      const cx = px - (box.left + box.width / 2);
      const cy = py - (box.top + box.height / 2);
      const ratio = scale / this.zoom.scale;
      this.zoom.x = cx - (cx - this.zoom.x) * ratio;
      this.zoom.y = cy - (cy - this.zoom.y) * ratio;
      this.zoom.scale = clamp(scale, 1, MAX_SCALE);
      this.applyZoom();
    }

    // ---- mouse ------------------------------------------------------

    onClick(e) {
      if (!finePointer.matches) return;
      if (!e.target.closest('.image-viewer__zoom')) return;
      if (this.zoom.scale > 1) {
        this.resetZoom();
      } else {
        this.zoomAt(TAP_ZOOM, e.clientX, e.clientY);
        this.followMouse(e);
      }
    }

    onMouseMove(e) {
      if (!finePointer.matches || this.zoom.scale <= 1) return;
      this.followMouse(e);
    }

    // Map the cursor's position in the slide to the pan range, so moving
    // to an edge shows that edge of the image.
    followMouse(e) {
      const cur = this.current();
      if (!cur) return;
      const box = cur.slide.getBoundingClientRect();
      const fx = (e.clientX - box.left) / box.width - 0.5;
      const fy = (e.clientY - box.top) / box.height - 0.5;
      const limits = this.panLimits();
      this.zoom.x = -fx * 2 * limits.x;
      this.zoom.y = -fy * 2 * limits.y;
      this.applyZoom();
    }

    // How far the zoomed photo can move before an edge would come into
    // view. offsetWidth/Height are the layout size, unaffected by the
    // zoom transform (and by its transition mid-animation).
    panLimits() {
      const cur = this.current();
      return {
        x: Math.max(0, (cur.img.offsetWidth * this.zoom.scale - cur.slide.clientWidth) / 2),
        y: Math.max(0, (cur.img.offsetHeight * this.zoom.scale - cur.slide.clientHeight) / 2),
      };
    }

    // ---- touch ------------------------------------------------------

    onTouchStart(e) {
      // Follow the fingers 1:1 — no easing while a gesture is running.
      this.root.classList.add('image-viewer--gesture');
      if (e.touches.length === 2) {
        e.preventDefault();
        const [a, b] = e.touches;
        this.pinch = {
          dist: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY),
          scale: this.zoom.scale,
        };
        this.pan = null;
      } else if (e.touches.length === 1 && this.zoom.scale > 1) {
        const t = e.touches[0];
        this.pan = { x: t.clientX, y: t.clientY, startX: this.zoom.x, startY: this.zoom.y };
      }
    }

    onTouchMove(e) {
      if (this.pinch && e.touches.length === 2) {
        e.preventDefault();
        const [a, b] = e.touches;
        const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        const midX = (a.clientX + b.clientX) / 2;
        const midY = (a.clientY + b.clientY) / 2;
        this.zoomAt(this.pinch.scale * (dist / this.pinch.dist), midX, midY);
      } else if (this.pan && e.touches.length === 1 && this.zoom.scale > 1) {
        // Zoomed: one finger pans the photo instead of swiping slides.
        e.preventDefault();
        const t = e.touches[0];
        this.zoom.x = this.pan.startX + (t.clientX - this.pan.x);
        this.zoom.y = this.pan.startY + (t.clientY - this.pan.y);
        this.applyZoom();
      }
    }

    onTouchEnd(e) {
      if (e.touches.length < 2) this.pinch = null;
      if (e.touches.length === 0) {
        this.pan = null;
        this.root.classList.remove('image-viewer--gesture');
      }

      // Double-tap: toggle zoom at the tap point.
      if (e.changedTouches.length !== 1 || e.touches.length !== 0) return;
      const t = e.changedTouches[0];
      const now = Date.now();
      const last = this.lastTap;
      if (last && now - last.time < DOUBLE_TAP_MS && Math.hypot(t.clientX - last.x, t.clientY - last.y) < 30) {
        this.lastTap = null;
        if (this.zoom.scale > 1) {
          this.resetZoom();
        } else {
          this.zoomAt(TAP_ZOOM, t.clientX, t.clientY);
        }
      } else {
        this.lastTap = { time: now, x: t.clientX, y: t.clientY };
      }
    }
  }

  let viewer = null;

  function galleryImages(list) {
    const items = Array.from(list.querySelectorAll('.product__media-item')).filter(
      (item) => item.offsetParent !== null && item.querySelector('.product-media-container.media-type-image img')
    );
    return items.map((item) => {
      const img = item.querySelector('.product-media-container.media-type-image .product__media img');
      return { item, src: img.currentSrc || img.src };
    });
  }

  document.addEventListener('click', (e) => {
    const container = e.target.closest('.product-media-container.media-type-image');
    if (!container) return;
    // Shoppable photos keep their tag taps.
    if (container.classList.contains('product-media-container--shoppable')) return;
    const list = container.closest('.product__media-list');
    if (!list || !list.closest('[data-image-viewer]')) return;

    const entries = galleryImages(list);
    const clickedItem = container.closest('.product__media-item');
    const start = Math.max(0, entries.findIndex((entry) => entry.item === clickedItem));
    if (!entries.length) return;

    e.preventDefault();
    viewer = viewer || new Viewer();
    viewer.open(
      entries.map((entry) => entry.src),
      start
    );
  });

  window.ProductImageViewer = { open: (srcs, start) => (viewer = viewer || new Viewer()).open(srcs, start || 0) };
})();
